import { AssessmentDraftingAnalytics } from './AssessmentDraftingAnalytics.js'

export class AssessmentQuestionSetUI {
  static mount(config) {
    if (window.MyEducator.questionSetDrafting) {
      window.MyEducator.questionSetDrafting.destroy()
    }
    window.MyEducator.questionSetDrafting = new AssessmentQuestionSetUI(config)
    window.MyEducator.questionSetDrafting.attachSetup()
    return window.MyEducator.questionSetDrafting
  }

  constructor(config) {
    this.config = config || {}
    this.root = $(this.config.rootSelector || '.ai-question-set-root').last()
    this.modal = this.root.closest('.modal')
    this.modalBody = this.root.closest('.modal-body')
    this.destinationType = this.config.destination || ''
    this.analytics = new AssessmentDraftingAnalytics(this.config)
    this.active = false
    this.allowClose = false
    this.destroyed = false
    this.requestGeneration = 0
    this.setupXhr = null
    this.generationXhr = null
    this.cleanupXhr = null
    this.orderXhr = null
    this.generationSlot = null
    this.saving = false
    this.planToken = ''
    this.slots = []
    this.currentIndex = 0
    this.candidates = {}
    this.candidateTokens = {}
    this.failures = {}
    this.saved = []
    this.discarded = []
    this.warning = ''
    this.destination = null
    this.cleanupAttempted = false
    this.destinationDeleted = false
    this.setupLoading = false
    this.countsValid = true
    this.candidateUrlTemplate = ''
    this.beforeUnloadHandler = event => {
      if (!this.active) return undefined
      event.preventDefault()
      event.returnValue = ''
      return ''
    }
  }

  attachSetup() {
    if (!this.root.length) return
    this.analytics.send('draft_v1_entry_opened', {}, 'entry-opened')
    const form = this.root.find('form.ai-question-set-setup')
    this.modal
      .off('hidden.bs.modal.question_set_setup')
      .one('hidden.bs.modal.question_set_setup', () => {
        if (!this.allowClose && this.active === false) {
          this.analytics.send(
            'draft_v1_workflow_closed',
            { close_mode: 'closed' },
            'workflow-closed'
          )
        }
        this.destroy()
      })
    this.updateTotal()
    this.syncAdditionalTypes()
    form
      .off('input.question_set_counts change.question_set_counts')
      .on(
        'input.question_set_counts change.question_set_counts',
        '.ai-question-set-counts input',
        () => {
          this.clearSetupErrors()
          this.updateTotal()
        }
      )
      .off('input.question_set_errors change.question_set_errors')
      .on(
        'input.question_set_errors change.question_set_errors',
        'input:not(.ai-question-set-count-input), textarea, select',
        () => this.clearSetupErrors()
      )
    this.root
      .off('click.question_set_count')
      .on(
        'click.question_set_count',
        '.ai-question-set-count-decrement, .ai-question-set-count-increment',
        event => this.adjustQuestionCount(event.currentTarget)
      )
      .on(
        'click.question_set_remove_type',
        '.ai-question-set-count-remove',
        event => this.removeQuestionType(event.currentTarget)
      )
    this.root
      .find('.ai-question-set-add-type-select')
      .off('change.question_set_add_type')
      .on('change.question_set_add_type', event => {
        this.addQuestionType($(event.currentTarget).val())
      })
    form.off('submit.question_set').on('submit.question_set', event => {
      event.preventDefault()
      this.start(form)
    })
    this.root
      .find('.ai-question-set-cancel')
      .off('click.question_set')
      .on('click.question_set', () => {
        this.analytics.send(
          'draft_v1_workflow_closed',
          { close_mode: 'closed' },
          'workflow-closed'
        )
        this.allowClose = true
        this.destroy()
        this.modal.modal('hide')
      })
  }

  updateTotal() {
    let total = 0
    let valuesAreValid = true
    this.root.find('.ai-question-set-counts input').each(function () {
      const value = Number($(this).val())
      if (!Number.isInteger(value) || value < 0 || value > 10) {
        valuesAreValid = false
        return
      }
      total += value
    })
    this.root.find('.ai-question-set-total span').text(total)
    const totalIsValid = valuesAreValid && total >= 1 && total <= 10
    this.countsValid = totalIsValid
    this.root
      .find('.ai-question-set-total')
      .toggleClass('is-invalid', !totalIsValid)
      .attr('aria-invalid', totalIsValid ? 'false' : 'true')
    const setupLoading = this.setupLoading
    this.root.find('.ai-question-set-count-control').each(function () {
      const control = $(this)
      const value = parseInt(control.find('input').val(), 10) || 0
      control
        .find('.ai-question-set-count-decrement')
        .prop('disabled', setupLoading || value <= 0)
      control
        .find('.ai-question-set-count-increment')
        .prop('disabled', setupLoading || value >= 10 || total >= 10)
    })
    this.root
      .find('.ai-question-set-start')
      .prop('disabled', this.setupLoading || !totalIsValid)
    return total
  }

  adjustQuestionCount(button) {
    if (this.setupLoading) return
    const control = $(button).closest('.ai-question-set-count-control')
    const input = control.find('input').first()
    const current = Math.min(10, Math.max(0, parseInt(input.val(), 10) || 0))
    const delta = $(button).hasClass('ai-question-set-count-increment') ? 1 : -1
    const total = this.updateTotal()
    if (delta > 0 && total >= 10) return
    input.val(Math.min(10, Math.max(0, current + delta))).trigger('change')
  }

  syncAdditionalTypes() {
    const select = this.root.find('.ai-question-set-add-type-select')
    if (!select.length) return
    this.root.find('.ai-question-set-count-card[data-additional="true"]').each(function () {
      const card = $(this)
      const key = card.attr('data-question-type')
      const selected = !card.hasClass('d-none')
      select.find(`option[value="${key}"]`).prop('disabled', selected)
    })
  }

  addQuestionType(questionType) {
    if (this.setupLoading || !questionType) return
    const card = this.root.find(
      `.ai-question-set-count-card[data-question-type="${questionType}"]`
    )
    if (!card.length) return
    const total = this.updateTotal()
    if (total >= 10) {
      this.showSetupError('The set can contain at most 10 questions.')
      this.root.find('.ai-question-set-add-type-select').val('')
      return
    }
    card.removeClass('d-none')
    card.find('input').val(1).trigger('change')
    this.root.find('.ai-question-set-add-type-select').val('')
    this.syncAdditionalTypes()
    card.find('input').trigger('focus')
  }

  removeQuestionType(button) {
    if (this.setupLoading) return
    const card = $(button).closest('.ai-question-set-count-card')
    card.find('input').val(0).trigger('change')
    card.addClass('d-none')
    this.syncAdditionalTypes()
  }

  start(form) {
    if (this.setupXhr) return
    const total = this.updateTotal()
    if (!this.countsValid || total < 1 || total > 10) {
      this.showSetupError('Choose between 1 and 10 questions.')
      return
    }
    this.clearSetupErrors()
    this.setSetupLoading(true)
    this.setupXhr = $.ajax({
      url: form.attr('action'),
      type: 'POST',
      dataType: 'json',
      data: form.serialize(),
    })
      .done(payload => {
        if (this.destroyed) return
        if (!payload || !payload.success) {
          this.showSetupError(
            (payload && payload.message) || 'Question drafting is unavailable.'
          )
          return
        }
        this.beginRun(payload)
      })
      .fail((xhr, textStatus) => {
        if (this.destroyed || textStatus === 'abort') return
        const payload = xhr.responseJSON || {}
        this.applySetupFieldErrors(payload.errors || {})
        this.showSetupError(
          this.errorWithReference(
            payload.message || 'Question drafting is unavailable.',
            payload.support_reference
          )
        )
      })
      .always(() => {
        this.setupXhr = null
        this.setSetupLoading(false)
      })
  }

  beginRun(payload) {
    this.planToken = payload.plan_token
    this.slots = payload.slots || []
    this.destinationType = payload.destination_type
    this.destination = payload.destination
    this.warning = payload.warning || ''
    this.candidateUrlTemplate = payload.candidate_url_template
    this.analytics.update({
      analyticsUrl: payload.analytics_url,
      workflowToken: payload.workflow_token,
    })
    this.currentIndex = 0
    this.active = true
    this.bindExitWarning()
    this.renderGenerating(0)
    this.generate(0, { display: true })
  }

  generate(slotIndex, { display = false, previousToken = '' } = {}) {
    if (this.destroyed || this.generationXhr || !this.slots[slotIndex]) return
    const slot = this.slots[slotIndex]
    const generation = ++this.requestGeneration
    this.generationSlot = slotIndex
    if (display) this.renderGenerating(slotIndex)
    const seenCandidates = Object.keys(this.candidateTokens).map(index => ({
      slot_index: parseInt(index, 10),
      token: this.candidateTokens[index],
    }))
    const url = this.candidateUrlTemplate.replace(
      '__TYPE__',
      encodeURIComponent(slot.question_type)
    )
    this.generationXhr = $.ajax({
      url,
      type: 'POST',
      dataType: 'json',
      data: {
        plan_token: this.planToken,
        slot_index: slotIndex,
        destination_type: this.destinationType,
        drafting_workflow_token: this.analytics.token,
        previous_candidate_token: previousToken,
        seen_candidates: JSON.stringify(seenCandidates),
      },
    })
      .done(payload => {
        if (this.destroyed || generation !== this.requestGeneration) return
        this.failures[slotIndex] = null
        this.candidates[slotIndex] = payload
        this.candidateTokens[slotIndex] = payload.candidate_token
        if (slotIndex === this.currentIndex) {
          this.renderCandidate(payload)
        }
      })
      .fail((xhr, textStatus) => {
        if (
          this.destroyed ||
          generation !== this.requestGeneration ||
          textStatus === 'abort'
        ) {
          return
        }
        const payload = xhr.responseJSON || {}
        this.failures[slotIndex] = {
          message: this.errorWithReference(
            payload.message || 'This question could not be drafted.',
            payload.support_reference
          ),
          retryCandidateToken: payload.retry_candidate_token || previousToken,
        }
        if (slotIndex === this.currentIndex) {
          this.renderFailure(slotIndex)
        }
      })
      .always(() => {
        if (generation !== this.requestGeneration) return
        this.generationXhr = null
        this.generationSlot = null
        if (this.candidates[this.currentIndex]) {
          this.prefetchNext()
        }
      })
  }

  prefetchNext() {
    const nextIndex = this.currentIndex + 1
    if (
      !this.active ||
      nextIndex >= this.slots.length ||
      this.candidates[nextIndex] ||
      this.failures[nextIndex] ||
      this.generationXhr
    ) {
      return
    }
    this.generate(nextIndex)
  }

  renderGenerating(slotIndex) {
    const slot = this.slots[slotIndex] || {}
    this.modalBody.html(`
      <div class="ai-question-set-review-state text-center" style="padding: 80px 20px;">
        <span class="fa-solid fa-spinner fa-spin fa-2x mb-3" aria-hidden="true"></span>
        <h4>Drafting question ${slotIndex + 1} of ${this.slots.length}</h4>
        <p class="text-muted">${this.escapeHtml(this.typeLabel(slot.question_type))}</p>
        ${this.warningHtml()}
      </div>
    `)
    this.scrollModalToTop()
  }

  renderCandidate(payload) {
    this.modalBody.html(payload.html)
    this.decorateCurrentEditor(payload)
    this.scrollModalToTop()
    this.prefetchNext()
  }

  decorateCurrentEditor(payload) {
    payload = payload || this.candidates[this.currentIndex]
    if (!payload || !this.active) return
    const form = this.modalBody.find('form').first()
    if (!form.length) return

    form.data('question-set-review', true)
    const nativeSaveLabel =
      form.find('.custom-form-submit-button').first().text().trim() || 'Save'
    form.find('.custom-form-submit-button').hide()
    const qtypeControls = form.find('[name="qtype"]')
    const nativeQuestionType = payload.native_question_type || payload.question_type
    qtypeControls.off('change.instructor_canvas_base change.question_set_type')
    qtypeControls.each(function () {
      const control = $(this)
      control.val(nativeQuestionType)
      if (control.attr('type') !== 'hidden') {
        $('<input>', {
          type: 'hidden',
          name: 'qtype',
          value: nativeQuestionType,
          class: 'ai-question-set-locked-qtype',
        }).insertAfter(control)
        control.prop('disabled', true).closest('.form-group').hide()
      }
    })

    this.modalBody.find('.ai-question-set-review-header').remove()
    const header = $(`
      <div class="ai-question-set-review-header mb-3">
        <div class="d-flex align-items-center justify-content-between">
          <div>
            <strong>Question ${this.currentIndex + 1} of ${this.slots.length}</strong>
            <span class="badge badge-secondary ml-2">${this.escapeHtml(
              payload.question_type_label
            )}</span>
          </div>
          <span class="text-muted">Review and edit before saving</span>
        </div>
        ${this.warningHtml()}
        <div class="alert alert-danger ai-question-set-save-error mt-2" style="display:none;"></div>
      </div>
    `)
    this.modalBody.prepend(header)

    const actions = $(`
      <div class="ai-question-set-review-actions d-flex justify-content-end mt-3 mb-3" style="gap: 8px;">
        <button type="button" class="btn btn-myed btn-default btn-default-myed ai-question-set-discard">Discard</button>
        <button type="button" class="btn btn-myed btn-default btn-default-myed ai-question-set-redraft">Redraft</button>
        <button type="button" class="btn btn-myed btn-primary btn-primary-myed ai-question-set-save">Save and continue</button>
      </div>
    `)
    form.after(actions)

    actions.find('.ai-question-set-save').on('click', () => {
      if (this.saving || form.data('ajax_submitting')) return
      this.saving = true
      actions.find('button').prop('disabled', true)
      // Successful native saves return a server-rendered script that calls
      // handleSaved with the trusted saved-question payload. Binding an AJAX
      // success handler here would run before that script and could advance twice.
      form
        .off('formlib.ajax_error.question_set')
        .one('formlib.ajax_error.question_set', (_event, _xhr, textStatus) => {
          if (textStatus === 'abort') return
          this.finishSaving()
          this.handleSaveError('This candidate could not be saved. Please try again.')
        })
      const submitField = form.find('[name="submit_button"]')
      submitField.val(nativeSaveLabel)
      form.trigger('submit')
      if (!form.data('ajax_submitting')) {
        this.finishSaving()
      }
    })
    actions.find('.ai-question-set-redraft').on('click', () => this.redraft())
    actions.find('.ai-question-set-discard').on('click', () => this.discard())
  }

  handleSaveError(message) {
    this.finishSaving()
    if (!this.modalBody.find('.ai-question-set-save-error').length) {
      this.decorateCurrentEditor()
    }
    this.modalBody
      .find('.ai-question-set-save-error')
      .text(message || 'This candidate could not be saved.')
      .show()
    this.scrollModalToTop()
  }

  handleInvalidSave() {
    this.finishSaving()
    this.decorateCurrentEditor()
    this.handleSaveError('Please check the highlighted fields and save again.')
  }

  handleSaved(payload) {
    this.finishSaving()
    if (!this.active || !payload || payload.slot_index !== this.currentIndex) {
      return
    }
    this.saved.push(payload)
    this.advance()
  }

  redraft() {
    if (this.saving) return
    const previousToken = this.candidateTokens[this.currentIndex] || ''
    this.analytics.send(
      'draft_v1_redraft_requested',
      { slot: this.currentIndex + 1, attempt: 'redraft' },
      `redraft:${this.currentIndex}:${this.requestGeneration + 1}`
    )
    this.abortGeneration()
    delete this.candidates[this.currentIndex]
    delete this.failures[this.currentIndex]
    this.generate(this.currentIndex, { display: true, previousToken })
  }

  discard() {
    if (this.saving) return
    this.discarded.push({
      slot_index: this.currentIndex,
      question_type: this.slots[this.currentIndex].question_type,
    })
    this.analytics.send(
      'draft_v1_candidate_discarded',
      { slot: this.currentIndex + 1 },
      `discard:${this.currentIndex}`
    )
    this.advance()
  }

  retry(slotIndex) {
    this.analytics.send(
      'draft_v1_candidate_retry_requested',
      { slot: slotIndex + 1 },
      `retry:${slotIndex}:${this.requestGeneration + 1}`
    )
    const failure = this.failures[slotIndex] || {}
    delete this.failures[slotIndex]
    this.generate(slotIndex, {
      display: true,
      previousToken: failure.retryCandidateToken || '',
    })
  }

  skip(slotIndex) {
    this.discarded.push({
      slot_index: slotIndex,
      question_type: this.slots[slotIndex].question_type,
      failed: true,
    })
    delete this.failures[slotIndex]
    this.analytics.send(
      'draft_v1_candidate_skipped',
      { slot: slotIndex + 1 },
      `skip:${slotIndex}`
    )
    this.advance()
  }

  advance() {
    this.currentIndex += 1
    if (this.currentIndex >= this.slots.length) {
      this.renderSummary()
      return
    }
    if (this.candidates[this.currentIndex]) {
      this.renderCandidate(this.candidates[this.currentIndex])
    } else if (this.failures[this.currentIndex]) {
      this.renderFailure(this.currentIndex)
    } else if (this.generationSlot === this.currentIndex) {
      this.renderGenerating(this.currentIndex)
    } else {
      this.generate(this.currentIndex, { display: true })
    }
  }

  renderFailure(slotIndex) {
    const failure = this.failures[slotIndex]
    this.modalBody.html(`
      <div class="ai-question-set-review-state" style="padding: 50px 20px;">
        <h4>Question ${slotIndex + 1} could not be drafted</h4>
        <div class="alert alert-danger">${this.escapeHtml(failure.message)}</div>
        <div class="d-flex justify-content-end" style="gap: 8px;">
          <button type="button" class="btn btn-myed btn-default btn-default-myed ai-question-set-skip">Skip</button>
          <button type="button" class="btn btn-myed btn-primary btn-primary-myed ai-question-set-retry">Retry</button>
        </div>
      </div>
    `)
    this.modalBody
      .find('.ai-question-set-retry')
      .on('click', () => this.retry(slotIndex))
    this.modalBody
      .find('.ai-question-set-skip')
      .on('click', () => this.skip(slotIndex))
    this.scrollModalToTop()
  }

  renderSummary() {
    this.active = false
    this.analytics.send(
      'draft_v1_workflow_completed',
      { completed_count: this.saved.length },
      'workflow-completed'
    )
    this.unbindExitWarning()
    this.abortGeneration()
    if (this.shouldCleanupEmptyDestination() && !this.cleanupAttempted) {
      this.modalBody.html(`
        <div class="ai-question-set-review-state text-center" style="padding: 80px 20px;">
          <span class="fa-solid fa-spinner fa-spin fa-2x mb-3" aria-hidden="true"></span>
          <h4>Removing the empty assessment</h4>
        </div>
      `)
      this.scrollModalToTop()
      this.cleanupEmptyDestination(() => this.renderSummary())
      return
    }
    const canReorder = Boolean(
      this.saved.length > 1 &&
        this.destination &&
        this.destination.order_url &&
        typeof $.fn.sortable === 'function'
    )
    const savedItems = this.saved
      .map(
        (item, index) => `
          <li class="ai-question-set-saved-item" data-guid="${this.escapeAttribute(item.guid)}">
            ${canReorder ? `
              <button type="button" class="ai-question-set-drag-handle" aria-label="Drag to reorder question ${index + 1}">
                <span class="fa-solid fa-grip-vertical" aria-hidden="true"></span>
              </button>
            ` : ''}
            <span class="ai-question-set-item-number">${index + 1}.</span>
            <div class="ai-question-set-item-content">
              <strong>${this.escapeHtml(item.question_type_label)}</strong>:
              ${this.escapeHtml(item.stem)}
              <button type="button" class="btn btn-link btn-sm ai-question-set-reopen" data-url="${this.escapeAttribute(item.editor_url)}">Reopen</button>
            </div>
          </li>
        `
      )
      .join('')
    const unsavedItems = this.discarded
      .map(
        item => `
          <li>
            Question ${item.slot_index + 1} —
            ${this.escapeHtml(this.typeLabel(item.question_type))}:
            ${item.failed ? 'Failed/skipped' : 'Discarded'}
          </li>
        `
      )
      .join('')
    this.modalBody.html(`
      <div class="ai-question-set-summary">
        <div class="ai-question-set-summary-content">
          <h3>Question set complete</h3>
          <p>${this.saved.length} saved; ${this.discarded.length} discarded or skipped.</p>
          ${this.warningHtml()}
          ${this.destinationStatusHtml()}
          ${canReorder ? '<p class="text-muted ai-question-set-order-help">Drag the saved questions to change their order.</p>' : ''}
          ${savedItems ? `<ol class="ai-question-set-saved-list">${savedItems}</ol>` : this.emptySummaryHtml()}
          <div class="ai-question-set-order-status" aria-live="polite"></div>
          ${unsavedItems ? `<h4>Not saved</h4><ul>${unsavedItems}</ul>` : ''}
        </div>
        <div class="ai-question-set-summary-actions">
          ${this.openAssessmentHtml()}
          <button type="button" class="btn btn-myed btn-primary btn-primary-myed ai-question-set-done">Done</button>
        </div>
      </div>
    `)
    this.modalBody.find('.ai-question-set-done').on('click', () => {
      this.allowClose = true
      this.modal.modal('hide')
    })
    this.modalBody.find('.ai-question-set-reopen').on('click', event => {
      const url = $(event.currentTarget).data('url')
      this.allowClose = true
      this.modal.one('hidden.bs.modal', () => {
        $.meDialog.open2({
          id: 'question_editor_dialog',
          url,
          title: 'Question Editor',
          width: 900,
        })
      })
      this.modal.modal('hide')
    })
    this.setupSummaryOrdering()
    this.scrollModalToTop()
  }

  setupSummaryOrdering() {
    const list = this.modalBody.find('.ai-question-set-saved-list')
    if (
      list.children().length < 2 ||
      !this.destination ||
      !this.destination.order_url ||
      typeof list.sortable !== 'function'
    ) {
      return
    }
    list.sortable({
      axis: 'y',
      handle: '.ai-question-set-drag-handle',
      placeholder: 'ai-question-set-sort-placeholder',
      update: () => this.persistSummaryOrder(list),
    })
  }

  persistSummaryOrder(list) {
    if (this.orderXhr) return
    const previousGuids = this.saved.map(item => String(item.guid))
    const orderedGuids = list
      .children('[data-guid]')
      .map(function () {
        return String($(this).data('guid'))
      })
      .get()
    if (
      orderedGuids.length !== previousGuids.length ||
      new Set(orderedGuids).size !== orderedGuids.length ||
      !orderedGuids.every(guid => previousGuids.includes(guid)) ||
      orderedGuids.every((guid, index) => guid === previousGuids[index])
    ) {
      return
    }

    this.setSummaryOrderStatus('Saving…', 'saving')
    list.sortable('disable')
    this.modalBody
      .find('.ai-question-set-summary a, .ai-question-set-summary button')
      .addClass('disabled')
      .prop('disabled', true)
    this.orderXhr = $.ajax({
      url: this.destination.order_url,
      type: 'POST',
      dataType: 'text',
      data: { guids: orderedGuids.join(',') },
    })
      .done(() => {
        const itemsByGuid = new Map(
          this.saved.map(item => [String(item.guid), item])
        )
        this.saved = orderedGuids.map(guid => itemsByGuid.get(guid))
        this.renumberSummaryQuestions(list)
        this.setSummaryOrderStatus('Order saved.', 'saved')
      })
      .fail((_xhr, textStatus) => {
        if (textStatus === 'abort') return
        this.restoreSummaryOrder(list, previousGuids)
        this.setSummaryOrderStatus('Could not save order.', 'error')
      })
      .always(() => {
        this.orderXhr = null
        list.sortable('enable')
        this.modalBody
          .find('.ai-question-set-summary a, .ai-question-set-summary button')
          .removeClass('disabled')
          .prop('disabled', false)
      })
  }

  restoreSummaryOrder(list, guids) {
    const itemsByGuid = {}
    list.children('[data-guid]').detach().each(function () {
      itemsByGuid[String($(this).data('guid'))] = this
    })
    guids.forEach(guid => list.append(itemsByGuid[guid]))
    this.renumberSummaryQuestions(list)
  }

  renumberSummaryQuestions(list) {
    list.children('[data-guid]').each(function (index) {
      $(this).find('.ai-question-set-item-number').text(`${index + 1}.`)
      $(this).find('.ai-question-set-drag-handle').attr(
        'aria-label',
        `Drag to reorder question ${index + 1}`
      )
    })
  }

  setSummaryOrderStatus(message, state) {
    this.modalBody
      .find('.ai-question-set-order-status')
      .attr('data-state', state || '')
      .text(message || '')
  }

  scrollModalToTop() {
    this.modalBody.scrollTop(0)
  }

  bindExitWarning() {
    window.addEventListener('beforeunload', this.beforeUnloadHandler)
    this.modal.off('hide.bs.modal.question_set').on('hide.bs.modal.question_set', event => {
      if (this.saving) {
        event.preventDefault()
        this.modalBody
          .find('.ai-question-set-save-error')
          .text('Wait for this question to finish saving before closing.')
          .show()
        return
      }
      if (this.cleanupXhr) {
        event.preventDefault()
        return
      }
      if (!this.active || this.allowClose) return
      if (!window.confirm('Discard the remaining AI-drafted questions? Saved questions will remain.')) {
        event.preventDefault()
        return
      }
      this.analytics.send(
        'draft_v1_workflow_closed',
        { close_mode: 'closed', completed_count: this.saved.length },
        'workflow-closed'
      )
      if (this.shouldCleanupEmptyDestination() && !this.cleanupAttempted) {
        event.preventDefault()
        this.abortGeneration()
        this.modalBody.html(`
          <div class="ai-question-set-review-state text-center" style="padding: 80px 20px;">
            <span class="fa-solid fa-spinner fa-spin fa-2x mb-3" aria-hidden="true"></span>
            <h4>Removing the empty assessment</h4>
          </div>
        `)
        this.cleanupEmptyDestination(deleted => {
          if (!deleted) {
            this.renderSummary()
            return
          }
          this.allowClose = true
          this.destroy(false)
          this.modal.modal('hide')
        })
        return
      }
      this.destroy()
    })
  }

  unbindExitWarning() {
    window.removeEventListener('beforeunload', this.beforeUnloadHandler)
    this.modal.off('hide.bs.modal.question_set')
  }

  abortGeneration() {
    this.requestGeneration += 1
    const xhr = this.generationXhr
    this.generationXhr = null
    this.generationSlot = null
    if (xhr && typeof xhr.abort === 'function') xhr.abort()
  }

  finishSaving() {
    this.saving = false
    this.modalBody.find('.ai-question-set-review-actions button').prop('disabled', false)
  }

  destroy(abort = true) {
    if (this.destroyed) return
    this.destroyed = true
    this.active = false
    this.unbindExitWarning()
    if (abort) {
      const setupXhr = this.setupXhr
      this.setupXhr = null
      if (setupXhr && typeof setupXhr.abort === 'function') setupXhr.abort()
      this.abortGeneration()
      const orderXhr = this.orderXhr
      this.orderXhr = null
      if (orderXhr && typeof orderXhr.abort === 'function') orderXhr.abort()
    }
    if (window.MyEducator.questionSetDrafting === this) {
      window.MyEducator.questionSetDrafting = null
    }
  }

  setSetupLoading(loading) {
    this.setupLoading = loading
    this.root.find('.ai-question-set-progress').toggle(loading)
    this.root
      .find('.ai-question-set-count-remove, .ai-question-set-add-type-select')
      .prop('disabled', loading)
    this.updateTotal()
  }

  clearSetupErrors() {
    this.root.find('.ai-question-set-error').hide().empty()
    this.root.find('.ai-question-set-field-error').empty()
  }

  applySetupFieldErrors(errors) {
    const fieldErrors = this.root.find('.ai-question-set-field-error')
    Object.keys(errors).forEach(field => {
      fieldErrors
        .filter((_index, element) => $(element).attr('data-field') === field)
        .text((errors[field] || []).join(' '))
    })
  }

  showSetupError(message) {
    this.root.find('.ai-question-set-error').text(message).show()
    this.scrollModalToTop()
  }

  errorWithReference(message, reference) {
    if (!reference || String(message).includes(reference)) return message
    return `${message} Support reference: ${reference}.`
  }

  warningHtml() {
    if (!this.warning) return ''
    const link = this.destination
      ? ` <a href="${this.escapeAttribute(this.destination.manual_embed_url)}">Open Assessments</a>`
      : ''
    return `<div class="alert alert-warning mt-3">${this.escapeHtml(this.warning)}${link}</div>`
  }

  destinationStatusHtml() {
    if (
      !this.destination ||
      !['reader', 'assessments'].includes(this.destinationType)
    ) {
      return ''
    }
    const availability = (
      'The assessment is Off and hidden from students until you enable it through Assessments.'
    )
    if (this.destinationType === 'reader' && this.destination.embedded_on_page) {
      return `
        <div class="alert alert-info mt-3">
          <strong>Placed on this textbook page.</strong>
          ${availability}
        </div>
      `
    }
    if (this.destinationType === 'reader' && !this.destination.reader_embed_requested) {
      return `
        <div class="alert alert-info mt-3">
          The assessment was created in Assessments and was not placed on this textbook page.
          ${availability}
        </div>
      `
    }
    return `<div class="alert alert-info mt-3">${availability}</div>`
  }

  shouldCleanupEmptyDestination() {
    return Boolean(
      this.saved.length === 0 &&
        this.destination &&
        this.destination.cleanup_url &&
        ['reader', 'assessments'].includes(this.destinationType)
    )
  }

  cleanupEmptyDestination(onComplete) {
    this.cleanupAttempted = true
    this.cleanupXhr = $.ajax({
      url: this.destination.cleanup_url,
      type: 'POST',
      dataType: 'json',
      data: {
        plan_token: this.planToken,
        destination_type: this.destinationType,
      },
    })
      .done(payload => {
        if (payload && payload.deleted) {
          this.destinationDeleted = true
          this.warning = ''
        } else {
          this.warning =
            'The empty hidden assessment could not be removed. You can manage it from Assessments.'
        }
      })
      .fail(xhr => {
        const payload = xhr.responseJSON || {}
        this.warning = this.errorWithReference(
          payload.message ||
            'The empty hidden assessment could not be removed. You can manage it from Assessments.',
          payload.support_reference
        )
      })
      .always(() => {
        const deleted = this.destinationDeleted
        this.cleanupXhr = null
        if (onComplete && !this.destroyed) onComplete(deleted)
      })
  }

  emptySummaryHtml() {
    return this.destinationDeleted
      ? '<p>No questions were saved, so the empty assessment was removed.</p>'
      : '<p>No questions were saved.</p>'
  }

  openAssessmentHtml() {
    if (this.destinationDeleted || !this.destination) return ''
    return `<a class="btn btn-myed btn-default btn-default-myed" href="${this.escapeAttribute(this.destination.assessment_url)}">Review assessment</a>`
  }

  typeLabel(questionType) {
    const labels = {
      multiplechoice: 'Multiple Choice',
      truefalse: 'True/False',
      shortanswer: 'Short Answer',
      essay: 'Essay',
    }
    return labels[questionType] || questionType || ''
  }

  escapeHtml(value) {
    return $('<div>').text(value == null ? '' : String(value)).html()
  }

  escapeAttribute(value) {
    return this.escapeHtml(value).replace(/"/g, '&quot;').replace(/'/g, '&#39;')
  }
}
